import React, { useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import {
  CloseOutlined,
  CheckCircleFilled,
  HistoryOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { fetchAllVersions, updateOneFormula } from "../../api/petFormulaApi";

interface FormulaCountry {
  id_version?: string;
  weights?: Record<string, number>;
  core_brands?: unknown[];
  companies?: string[];
  competitors?: unknown[];
  start_end_month?: string[];
}

interface VersionSummary {
  version_id: string;
  version_name: string;
  comment?: string;
  created_by: string;
  created_at: string;
  is_active: boolean;
  formula: Record<string, FormulaCountry>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onRestoreSuccess: () => void;
  userData: unknown;
  selectedWorkspace: unknown;
  authIdClient: string | null;
  isPipelineRunning?: boolean;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function versionLabel(versionId: string): string {
  const num = parseInt(versionId.split("_").pop() ?? "0", 10);
  return `v${num}`;
}

/** Transform Firestore formula → update API payload */
function buildRestorePayload(version: VersionSummary) {
  const countries = Object.entries(version.formula).map(([country, data]) => ({
    country,
    weights: data.weights ?? {},
    core_brands: data.core_brands ?? [],
    companies: data.companies ?? [],
    competitors: data.competitors ?? [],
    start_end_month: data.start_end_month ?? null,
  }));
  return {
    countries,
    comment: `Restored from ${versionLabel(version.version_id)}`,
  };
}

type View = "list" | "confirm";

const ConfigVersionHistoryModal: React.FC<Props> = ({
  open,
  onClose,
  onRestoreSuccess,
  userData,
  selectedWorkspace,
  authIdClient,
  isPipelineRunning = false,
}) => {
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>("list");
  const [selectedVersion, setSelectedVersion] = useState<VersionSummary | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setView("list");
      setSelectedVersion(null);
      setRestoreError(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetchAllVersions(userData, selectedWorkspace, authIdClient)
      .then((res: { versions: VersionSummary[] }) => setVersions(res.versions ?? []))
      .catch(() => setError("Failed to load version history. Please try again."))
      .finally(() => setLoading(false));
  }, [open, userData, selectedWorkspace, authIdClient]);

  const handleRestoreClick = (version: VersionSummary) => {
    setSelectedVersion(version);
    setRestoreError(null);
    setView("confirm");
  };

  const handleConfirmRestore = async () => {
    if (!selectedVersion) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      const payload = buildRestorePayload(selectedVersion);
      await updateOneFormula(payload, userData, selectedWorkspace, authIdClient);
      onRestoreSuccess();
      onClose();
    } catch {
      setRestoreError("Restore failed. Please try again.");
    } finally {
      setRestoring(false);
    }
  };

  const title = view === "confirm" ? "Confirm Restore" : "Configuration History";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      maskClosable={false}
      closable={false}
      styles={{ body: { padding: "0 24px 24px" } }}
    >
      {/* Header */}
      <div className="flex items-center justify-between pt-6 pb-5">
        <div className="flex items-center gap-2">
          {view === "confirm" && (
            <button
              onClick={() => setView("list")}
              className="size-6 flex items-center justify-center hover:opacity-70 mr-1"
              aria-label="Back"
            >
              <ArrowLeftOutlined style={{ color: "rgba(0,0,0,0.5)", fontSize: 14 }} />
            </button>
          )}
          <HistoryOutlined style={{ fontSize: 18, color: "#002882" }} />
          <span className="text-[#0a0a0a] text-xl font-semibold font-[Satoshi]">
            {title}
          </span>
        </div>
        <button
          onClick={onClose}
          className="size-6 flex items-center justify-center hover:opacity-70"
          aria-label="Close"
        >
          <CloseOutlined style={{ color: "rgba(0,0,0,0.4)", fontSize: 14 }} />
        </button>
      </div>

      {/* List view */}
      {view === "list" && (
        <>
          <p className="text-[rgba(0,0,0,0.45)] text-sm mb-5 font-[Inter]">
            Up to 3 saved versions are kept. Restoring a version triggers a new pipeline run with that config.
          </p>

          {isPipelineRunning && (
            <div className="mb-4 px-3 py-2 rounded-lg font-[Inter]" style={{ backgroundColor: "#FFF7E6", border: "1px solid #FFD591" }}>
              <span className="text-xs font-medium" style={{ color: "#D46B08" }}>
                A pipeline is currently running. Restore is disabled until it completes.
              </span>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Spin size="large" />
            </div>
          ) : error ? (
            <div className="text-center py-10 text-[rgba(0,0,0,0.45)] text-sm">{error}</div>
          ) : versions.length === 0 ? (
            <div className="text-center py-10 text-[rgba(0,0,0,0.45)] text-sm">
              No saved versions found.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {versions.map((v) => (
                <div
                  key={v.version_id}
                  className="rounded-2xl border p-4 flex items-start justify-between gap-4"
                  style={{
                    borderColor: v.is_active ? "#002882" : "#f0f0f0",
                    backgroundColor: v.is_active ? "#f0f4ff" : "#fafafa",
                    borderLeftWidth: v.is_active ? 3 : 1,
                  }}
                >
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-xs font-semibold font-[Inter] px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: v.is_active ? "#002882" : "#e5e7eb",
                          color: v.is_active ? "#ffffff" : "#374151",
                        }}
                      >
                        {versionLabel(v.version_id)}
                      </span>
                      {v.is_active && (
                        <span className="flex items-center gap-1 text-xs font-medium font-[Inter]" style={{ color: "#22c55e" }}>
                          <CheckCircleFilled style={{ fontSize: 12 }} />
                          Active
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-xs text-[rgba(0,0,0,0.35)] font-[Inter]">
                        {formatDate(v.created_at)}
                      </span>
                      <span className="text-[rgba(0,0,0,0.2)] text-xs">·</span>
                      <span className="text-xs text-[rgba(0,0,0,0.35)] font-[Inter]">
                        {v.created_by}
                      </span>
                      <span className="text-[rgba(0,0,0,0.2)] text-xs">·</span>
                      <span className="text-xs text-[rgba(0,0,0,0.35)] font-[Inter]">
                        {Object.keys(v.formula).length} market{Object.keys(v.formula).length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>

                  {!v.is_active && (
                    <button
                      onClick={() => handleRestoreClick(v)}
                      disabled={isPipelineRunning}
                      className="shrink-0 text-sm font-medium font-[Inter] px-4 py-1.5 rounded-full border transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#002882] hover:text-white disabled:hover:bg-transparent disabled:hover:text-[#002882]"
                      style={{ borderColor: "#002882", color: "#002882", fontSize: "0.8125rem" }}
                    >
                      Restore
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Confirm restore view */}
      {view === "confirm" && selectedVersion && (
        <div className="flex flex-col gap-4">
          <div
            className="rounded-2xl border p-4"
            style={{ borderColor: "#f0f0f0", backgroundColor: "#fafafa" }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span
                className="text-xs font-semibold font-[Inter] px-2 py-0.5 rounded-full"
                style={{ backgroundColor: "#e5e7eb", color: "#374151" }}
              >
                {versionLabel(selectedVersion.version_id)}
              </span>
            </div>
            <p className="text-xs text-[rgba(0,0,0,0.35)] font-[Inter] mt-2">
              {formatDate(selectedVersion.created_at)} · {selectedVersion.created_by} · {Object.keys(selectedVersion.formula).length} markets
            </p>
          </div>

          <p className="text-sm text-[rgba(0,0,0,0.65)] font-[Inter]">
            This will restore <strong>all markets, weights, and core brands</strong> from this version and trigger a new pipeline run.
          </p>

          {restoreError && (
            <p className="text-sm font-[Inter]" style={{ color: "#ff4d4f" }}>
              {restoreError}
            </p>
          )}

          <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="text-sm text-[rgba(0,0,0,0.9)] font-[Inter] leading-5">
              <span>Please note: <br />The data refresh may take a few minutes to over an hour, depending on the size of the data. </span>
              <span className="font-semibold">No further configuration changes can be made while it is pending.</span>
              <span> You are free to leave this page — the update will continue in the background.</span>
            </div>
            <div className="text-sm text-[rgba(0,0,0,0.9)] font-[Inter] leading-5">
              You will continue to see your current version of the data in all views until the update is complete.
            </div>
            <div style={{ padding: "8px 12px", borderRadius: 8, backgroundColor: "#EFF4FF", border: "1px solid #C7D9FF" }}>
              <span className="text-sm font-medium font-[Inter]" style={{ color: "#2B5BB8" }}>
                You will receive an email notification when the data update is complete.
              </span>
            </div>
            {versions.length >= 3 && (
              <div style={{ padding: "8px 12px", borderRadius: 8, backgroundColor: "#FFF7E6", border: "1px solid #FFD591" }}>
                <span className="text-sm font-medium font-[Inter]" style={{ color: "#D46B08" }}>
                  You have reached the maximum of 3 saved versions. The oldest version will be permanently removed when you save.
                </span>
              </div>
            )}
            <div className="text-sm text-[rgba(0,0,0,0.9)] font-[Inter] leading-5">
              Are you sure you want to proceed?
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <button
              onClick={() => setView("list")}
              disabled={restoring}
              className="px-5 py-2 rounded-full border text-sm font-medium font-[Inter] hover:bg-gray-50 disabled:opacity-50"
              style={{ borderColor: "#d9d9d9", color: "rgba(0,0,0,0.65)" }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmRestore}
              disabled={restoring}
              className="px-5 py-2 rounded-full text-sm font-medium font-[Inter] text-white flex items-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: "#002882" }}
            >
              {restoring && <Spin size="small" />}
              Restore & Run Pipeline
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ConfigVersionHistoryModal;
